import { useState, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import {
  Stack,
  Stepper,
  Text,
  TagsInput,
  Box,
  Group,
  Button,
  ScrollArea,
} from '@mantine/core';
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import { useSelection } from '@helpers/hooks/useSelection';
import {
  useRedactSearchLazyQuery,
  useRedactEntitiesJobMutation,
  RedactEntity,
  RedactionStatusType,
  ListResourcesDocument,
  ListResourcesQueryVariables,
  Redaction,
} from '@app-types/blackroom-api';
import {
  DialogRedactionProps,
  DialogRedactionSteps,
  dialogRedactionSteps,
} from './types';
import ResponsiveStepper from '@app/shared/ui-kit/molecules/ResponsiveStepper';
import { palette } from '@app/shared/ui-kit/colors';
import { dialogRedaction, entitiesGroup, searchStepGrid } from './styles.css';
import RedactionChip from '../RedactionChip';
import NoData from '@app/shared/ui-kit/atoms/NoData';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogRedaction({
  resourcesToRedact = [],
  ...props
}: DialogRedactionProps) {
  /* ********************************** HOOKS ********************************* */

  // Context
  const workspace = useWorkspace();

  // State
  const [keywords, setKeywords] = useState<string[]>([]);
  const [entities, setEntities] = useState<RedactEntity[]>([]);
  const [redactSearchResults, setRedactSearchResults] = useState<
    Pick<Redaction, 'id' | 'entity' | 'matchData' | 'status'>[]
  >([]);

  const {
    selectedIds: selectedRedactionIds,
    toggle: toggleRedactionSelection,
    selectAll: selectAllRedactions,
    deselectAll: deselectAllRedactions,
    setSelection: setSelectedRedactionIds,
  } = useSelection<Pick<Redaction, 'id'>>({ getId: (r) => r.id });

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = useMemo(
    () => dialogRedactionSteps[currentStepIndex],
    [currentStepIndex],
  );

  // Separate already-redacted from selectable results
  const { selectableResults, alreadyRedactedResults } = useMemo(() => {
    const selectable = redactSearchResults.filter(
      (r) => r.status !== RedactionStatusType.Redacted,
    );
    const alreadyRedacted = redactSearchResults.filter(
      (r) => r.status === RedactionStatusType.Redacted,
    );
    return {
      selectableResults: selectable,
      alreadyRedactedResults: alreadyRedacted,
    };
  }, [redactSearchResults]);

  const canConfirmStep = useMemo(() => {
    switch (currentStep) {
      case 'search':
        return entities.length > 0 || keywords.length > 0;
      case 'results':
        return selectedRedactionIds.size > 0;
    }
  }, [currentStep, selectedRedactionIds, entities, keywords]);

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);

  // API
  const [redactSearch, { loading: loadingRedactSearch }] =
    useRedactSearchLazyQuery();
  const [redactEntitiesJob, { loading: loadingRedactEntitiesJob }] =
    useRedactEntitiesJobMutation();
  const [isRedacting, setIsRedacting] = useState(false);

  // Localization
  const { t } = useTranslation();

  /* ***************************** LOCAL VARIABLES **************************** */

  const removeEntity = (entity: RedactEntity) => {
    if (entities.includes(entity)) {
      setEntities(entities.filter((e) => e !== entity));
    } else {
      setEntities([...entities, entity]);
    }
  };

  const confirmSteps: Record<DialogRedactionSteps, () => any> = {
    search: async () => {
      const { data: redactSearchResultData } = await redactSearch({
        variables: {
          workspaceId: workspace.id,
          files: resourcesToRedact.map((r) => r.id),
          entities: entities.map((entity) => ({ entity })),
          keywords,
        },
        fetchPolicy: 'no-cache',
      });
      if (redactSearchResultData) {
        const results = redactSearchResultData.redactSearchResults;
        setRedactSearchResults(results);
        // Select only non-redacted results by default
        const selectable = results.filter(
          (r) => r.status !== RedactionStatusType.Redacted,
        );
        setSelectedRedactionIds(selectable.map((r) => r.id));
        setCurrentStepIndex(currentStepIndex + 1);
      }
    },
    results: async () => {
      try {
        setIsRedacting(true);

        const resourceIdsToRedact = resourcesToRedact.map((r) => r.id);

        const refetchQueries = [
          {
            query: ListResourcesDocument,
            variables: {
              filter: {
                workspaceId: workspace.id,
                ids: resourceIdsToRedact,
              },
              limit: resourceIdsToRedact.length,
              offset: 0,
            } satisfies ListResourcesQueryVariables,
            fetchPolicy: 'network-only',
          } as const,
        ];

        await redactEntitiesJob({
          variables: {
            filter: {
              workspaceId: workspace.id,
              entities: entities,
              resourceIds: resourceIdsToRedact,
              keywords: keywords,
            },
            action: 'redact',
            include: Array.from(selectedRedactionIds),
          },
          refetchQueries,
        });

        showNotification({
          title: t('Resource:Common.operation.redact.sentByEmail.title'),
          message: t('Resource:Common.operation.redact.sentByEmail.message'),
          color: 'blackroomBlue',
        });
      } catch (error) {
        showNotification({
          title: t('Common:errors.genericTitle'),
          message: t('Common:errors.genericMessage'),
          color: 'blackroomRed',
        });
        console.error(error);
      }

      dialogStore?.closeDialog?.('dialog-redaction');
    },
  };

  /* ***************************** RENDER HELPERS ***************************** */

  function renderSearchStep() {
    const SearchStepLabel = ({ children }: { children: React.ReactNode }) => (
      <Group h={42} align="center">
        {children}
      </Group>
    );

    return (
      <ScrollArea
        w="100%"
        h="100%"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Text
          fz={24}
          fw="bold"
          ta="center"
          c={palette.blackroomBlack}
          py="xs"
          pb="lg"
        >
          {t('Redaction:DialogRedaction.selectRedactPatterns')}
        </Text>

        <Box className={searchStepGrid} w="100%">
          {/* Entities */}
          <SearchStepLabel>
            {t('Redaction:Common.redactSearchArgs.entities')}
          </SearchStepLabel>
          <Group className={entitiesGroup}>
            {Object.values(RedactEntity)
              .filter(
                (entity) =>
                  entity !== RedactEntity.Keyword &&
                  entity !== RedactEntity.Manual,
              )
              .map((entity) => (
                <Button
                  key={entity}
                  size="sm"
                  color="blackroomBlack"
                  variant={entities.includes(entity) ? 'filled' : 'default'}
                  onClick={() => removeEntity(entity)}
                >
                  {t(`Redaction:Common.redactionEntity.${entity}`)}
                </Button>
              ))}
          </Group>

          {/* Keywords */}
          <SearchStepLabel>
            {t('Redaction:Common.redactSearchArgs.keywords')}
          </SearchStepLabel>
          <TagsInput
            size="md"
            h={52}
            py={0}
            variant="unstyled"
            splitChars={[';']}
            value={keywords}
            placeholder={t(
              'Redaction:DialogRedaction.search.insertKeywordPlaceholder',
            )}
            onChange={(value) => setKeywords(value)}
          />
        </Box>
      </ScrollArea>
    );
  }

  function renderResultsStep() {
    if (redactSearchResults.length === 0) {
      return (
        <NoData
          title={t('Redaction:DialogRedaction.confirm.noData.title') as string}
          message={
            t('Redaction:DialogRedaction.confirm.noData.message') as string
          }
        />
      );
    }

    const allSelectableSelected =
      selectedRedactionIds.size === selectableResults.length &&
      selectableResults.length > 0;

    return (
      <ScrollArea h="100%" style={{ flex: 1 }}>
        <Stack gap="lg">
          {/* New redactions to apply */}
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text fz={14} fw={600} c={palette.blackroomBlack}>
                {t('Redaction:DialogRedaction.newRedactionsToApply')}
                {selectableResults.length > 0 && (
                  <Text span fz={12} fw={400} c={palette.blackroomGray}>
                    {' '}
                    — {selectedRedactionIds.size}/{selectableResults.length}
                  </Text>
                )}
              </Text>
              {selectableResults.length > 0 && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="blackroomBlack"
                  onClick={
                    allSelectableSelected
                      ? deselectAllRedactions
                      : () => selectAllRedactions(selectableResults)
                  }
                >
                  {allSelectableSelected
                    ? t('Common:cta.deselectAll')
                    : t('Common:cta.selectAll')}
                </Button>
              )}
            </Group>
            {selectableResults.length > 0 ? (
              <Group gap={10}>
                {selectableResults.map((redaction) => (
                  <RedactionChip
                    key={redaction.id}
                    redaction={redaction}
                    withPreview
                    selected={selectedRedactionIds.has(redaction.id)}
                    onToggle={() => toggleRedactionSelection(redaction.id)}
                  />
                ))}
              </Group>
            ) : (
              <Text fz={12} c={palette.blackroomGray}>
                {t('Redaction:DialogRedaction.confirm.noData.message')}
              </Text>
            )}
          </Stack>

          {/* Already applied redactions */}
          {alreadyRedactedResults.length > 0 && (
            <Stack gap="xs">
              <Text fz={14} fw={600} c={palette.blackroomBlack}>
                {t('Redaction:DialogRedaction.alreadyAppliedRedactions')}
                <Text span fz={12} fw={400} c={palette.blackroomGray}>
                  {' '}
                  — {alreadyRedactedResults.length}
                </Text>
              </Text>
              <Group gap={10}>
                {alreadyRedactedResults.map((redaction) => (
                  <RedactionChip
                    key={redaction.id}
                    redaction={redaction}
                    withPreview
                    disabled
                  />
                ))}
              </Group>
            </Stack>
          )}
        </Stack>
      </ScrollArea>
    );
  }

  function renderCurrentStep() {
    const renderer: Record<DialogRedactionSteps, () => any> = {
      search: renderSearchStep,
      results: renderResultsStep,
    };
    return renderer[currentStep]();
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Stack className={dialogRedaction}>
      <ResponsiveStepper
        w="100%"
        maw={450}
        flex={0}
        px="lg"
        active={dialogRedactionSteps.indexOf(currentStep)}
        onStepClick={(stepIndex) => setCurrentStepIndex(stepIndex)}
        allowNextStepsSelect={false}
      >
        {dialogRedactionSteps.map((step, index) => (
          <Stepper.Step
            key={step}
            completedIcon={<Text>{index + 1}</Text>}
            label={t(`Redaction:DialogRedaction.steps.${step}.title.label`)}
          />
        ))}
      </ResponsiveStepper>

      <Stack flex={1} mih={0} w="100%" justify="start">
        {renderCurrentStep()}
      </Stack>

      <ConfirmBar
        w="100%"
        onConfirm={confirmSteps[currentStep]}
        onCancel={() =>
          currentStepIndex === 0
            ? dialogStore?.closeDialog?.('dialog-redaction')
            : setCurrentStepIndex(currentStepIndex - 1)
        }
        saving={loadingRedactEntitiesJob || loadingRedactSearch || isRedacting}
        disabled={!canConfirmStep}
        labelProps={{
          cancel:
            currentStepIndex === 0
              ? t('Common:prompts.cancel')
              : t('Common:cta.back'),
          confirm:
            currentStepIndex === dialogRedactionSteps.length - 1
              ? t('Common:prompts.confirm')
              : t('Common:cta.saveContinue'),
        }}
      />
    </Stack>
  );
}

export default DialogRedaction;
